import { Broadcast } from '../types/broadcast.types';

export const getStatusColor = (status: string, isScheduled?: boolean): string => {
    if (status === 'pending' && isScheduled) {
        return 'bg-cyan-100 text-cyan-800 border-cyan-200';
    }
    switch (status) {
        case 'pending':
            return 'bg-yellow-100 text-yellow-800 border-yellow-200';
        case 'processing':
            return 'bg-blue-100 text-blue-800 border-blue-200';
        case 'sent':
            return 'bg-green-100 text-green-800 border-green-200';
        case 'partial':
            return 'bg-amber-100 text-amber-900 border-amber-200';
        case 'failed':
            return 'bg-red-100 text-red-800 border-red-200';
        case 'cancelled':
            return 'bg-gray-100 text-gray-800 border-gray-200';
        case 'translating':
            return 'bg-purple-100 text-purple-800 border-purple-200';
        default:
            return 'bg-gray-100 text-gray-800 border-gray-200';
    }
};

export const getStatusLabel = (status: string, isScheduled?: boolean): string => {
    if (status === 'pending' && isScheduled) {
        return 'Agendado';
    }
    switch (status) {
        case 'pending':
            return 'Pendente';
        case 'processing':
            return 'Enviando';
        case 'sent':
            return 'Enviado';
        case 'partial':
            return 'Enviado parcialmente';
        case 'failed':
            return 'Falhou';
        case 'cancelled':
            return 'Cancelado';
        case 'translating':
            return 'Traduzindo';
        default:
            return status;
    }
};

export const sortBroadcasts = (
    broadcasts: Broadcast[],
    sortBy: 'sent_count' | 'success_rate' | 'created_at',
    sortOrder: 'asc' | 'desc'
): Broadcast[] => {
    return [...broadcasts].sort((a, b) => {
        let comparison = 0;

        switch (sortBy) {
            case 'sent_count':
                comparison = (a.sent_count || 0) - (b.sent_count || 0);
                break;
            case 'success_rate':
                comparison = (a.success_rate || 0) - (b.success_rate || 0);
                break;
            case 'created_at':
                comparison = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
                if (comparison === 0) comparison = a.id - b.id;
                break;
        }

        return sortOrder === 'asc' ? comparison : -comparison;
    });
};

export const filterBroadcasts = (
    broadcasts: Broadcast[],
    page?: string,
    status?: string,
    targetLanguage?: string
): Broadcast[] => {
    return broadcasts.filter(broadcast => {
        if (page && !broadcast.pages.some(p => p.page_id === page)) {
            return false;
        }
        if (status && broadcast.status !== status) {
            return false;
        }
        if (targetLanguage) {
            if (targetLanguage === '__none__') {
                // Only broadcasts without AI translation
                if (broadcast.ai_translate) return false;
            } else {
                // Broadcasts with a specific target language
                if (!broadcast.ai_translate || broadcast.target_language !== targetLanguage) return false;
            }
        }
        return true;
    });
};

export const formatSuccessRate = (rate?: number): string => {
    if (rate === undefined || rate === null) return '--';
    return `${rate.toFixed(1)}%`;
};


// ──────────────────────────────────────────────────────────
//  Tipo de Envio (deriva do campo `message_tag` salvo no banco)
// ──────────────────────────────────────────────────────────

export type BroadcastTypeKind = 'standard' | 'utility' | 'message_tag';

export interface BroadcastTypeInfo {
    kind: BroadcastTypeKind;
    /** Label curto pra exibir (Standard / Utility / nome da tag custom) */
    label: string;
    /** Tag específica — só faz sentido pra kind=message_tag */
    tag: string | null;
}

/**
 * `ACCOUNT_UPDATE` é a tag de Utility do Messenger (mapeada pra messaging_type=UTILITY).
 * `CONFIRMED_EVENT_UPDATE` mantido por retrocompat com broadcasts antigos.
 * `null` = Standard. Qualquer outra string = tag custom (kind=message_tag).
 */
export const getBroadcastTypeInfo = (
    messageTag: string | null | undefined
): BroadcastTypeInfo => {
    if (!messageTag) {
        return { kind: 'standard', label: 'Standard', tag: null };
    }
    if (messageTag === 'ACCOUNT_UPDATE' || messageTag === 'CONFIRMED_EVENT_UPDATE') {
        return { kind: 'utility', label: 'Utility', tag: null };
    }
    return { kind: 'message_tag', label: messageTag, tag: messageTag };
};

/**
 * Formata um timestamp ISO convertendo de UTC pra fuso local do navegador.
 *
 * O backend agora grava TODOS os timestamps em UTC (`scheduled_at`,
 * `created_at`, `started_at`, etc), e serializa com `+00:00`. Aqui só
 * delegamos pro `toLocaleString` do browser que aplica o fuso local.
 *
 * Histórico: essa função antes mostrava o valor LITERAL (sem conversão),
 * porque o backend gravava o wall-clock "fake-UTC" — string igual ao que
 * o user digitava. Depois do fix de fuso (driver UTC + client_tz no
 * payload de criação), o valor passou a ser UTC real, então conversão
 * via `toLocaleString` é o caminho certo.
 *
 * Aceita: "2026-05-13T23:51:00+00:00", "2026-05-13T23:51:00Z", "2026-05-13 23:51:00"
 * Retorna: "13/05/26, 20:51" (em BRT, se browser estiver em São Paulo)
 */
export const formatWallClockDate = (iso: string | null | undefined): string => {
    if (!iso) return '';
    // Se a string vem sem timezone (formato "YYYY-MM-DD HH:MM:SS"), o
    // backend deveria sempre mandar com `+00:00` — mas pra robustez,
    // assumimos UTC quando vier sem indicador.
    const s = String(iso);
    const hasTz = /[+-]\d{2}:\d{2}$|Z$/.test(s);
    const normalized = hasTz ? s : s.replace(' ', 'T') + 'Z';
    try {
        return new Date(normalized).toLocaleString('pt-BR', {
            day: '2-digit', month: '2-digit', year: '2-digit',
            hour: '2-digit', minute: '2-digit',
        });
    } catch {
        return s;
    }
};

/**
 * Janela de edição (em minutos) — broadcast agendado fica bloqueado quando
 * faltar menos que isso pro envio. Espelha SCHEDULED_EDIT_CUTOFF_MINUTES do
 * backend.
 */
export const SCHEDULED_EDIT_CUTOFF_MIN = 5;

/**
 * Parseia `scheduled_at` (wall clock) como timestamp UTC pra comparação.
 * Trata "2026-05-13T23:51:00+00:00" e "2026-05-13 23:51:00" como UTC naive.
 */
const parseScheduledAtAsUTC = (iso: string): number | null => {
    const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/);
    if (!m) return null;
    const [, y, mo, d, h, mi, s] = m;
    return Date.UTC(+y, +mo - 1, +d, +h, +mi, +(s || 0));
};

/**
 * Checa se um broadcast (ou grupo) pode ser editado neste momento.
 *
 * Regras (espelham o backend):
 *   - Status precisa ser 'pending' ou 'translating'
 *   - Se agendado: faltar pelo menos 5 minutos até o scheduled_at
 *   - Não-agendado pendente: sempre editável
 *
 * Returns: { editable, reason } — reason explica por que NÃO está editável.
 */
export interface EditabilityCheck {
    editable: boolean;
    reason: string | null;
    /** Minutos restantes até o cutoff (null se não-agendado) */
    minutesUntilCutoff: number | null;
}

export const checkBroadcastEditable = (params: {
    status: string;
    is_scheduled?: boolean | null;
    scheduled_at?: string | null;
}): EditabilityCheck => {
    const status = (params.status || '').toLowerCase();
    if (!['pending', 'translating'].includes(status)) {
        return { editable: false, reason: `Status não-editável: ${status}`, minutesUntilCutoff: null };
    }
    if (!params.is_scheduled || !params.scheduled_at) {
        return { editable: true, reason: null, minutesUntilCutoff: null };
    }
    const schedMs = parseScheduledAtAsUTC(params.scheduled_at);
    if (!schedMs) {
        return { editable: true, reason: null, minutesUntilCutoff: null };
    }
    const nowMs = Date.now();
    const minutesUntil = Math.floor((schedMs - nowMs) / 60000);
    if (minutesUntil < SCHEDULED_EDIT_CUTOFF_MIN) {
        return {
            editable: false,
            reason: `Bloqueado — faltam menos de ${SCHEDULED_EDIT_CUTOFF_MIN} min pro envio`,
            minutesUntilCutoff: minutesUntil,
        };
    }
    return { editable: true, reason: null, minutesUntilCutoff: minutesUntil };
};
